import { flip } from '@floating-ui/react-dom';
import React, { useCallback } from 'react';
import * as styles from './ContentPreviewPopOver.module.scss';
import ErrorBoundary from './ErrorBoundary';
import PopOverArrow from './PopOverArrow';
import type { PopOverPrimitiveAllowControlledProps } from './PopOverPrimitive';
import PopOverPrimitive from './PopOverPrimitive';
import contentPreviewPopOverSize from '../utils/popover/contentPreviewPopOverSize';
import { useIsSize } from '../utils/responsive/isSize';
import Sizes from '../utils/responsive/Sizes';

export const SCREEN_EDGE_PADDING = 12;

type ContentPreviewPopOverProps = {
  hideOnMobile?: boolean;
  hoverDisabled?: boolean;
  children?: React.ReactNode;
  popOver: React.ReactNode;
  allowCrossAxisFlip?: boolean;
  minWidth?: number;
  minHeight?: number;
  offset?: number;
  display?: React.ComponentProps<typeof PopOverPrimitive.Trigger>['display'];
  floatingRef?: React.RefAttributes<HTMLDivElement>['ref'];
} & Pick<
  React.ComponentProps<typeof PopOverPrimitive>,
  'placement' | 'hidden' | 'hoverGroupId' | 'anchorRef'
> &
  PopOverPrimitiveAllowControlledProps;

const ContentPreviewPopOver = ({
  hideOnMobile,
  popOver,
  children,
  hoverDisabled,
  placement = 'right',
  allowCrossAxisFlip,
  minWidth = 325,
  minHeight = 250,
  display = 'block',
  offset: offsetAmount = 10,
  floatingRef,
  ...props
}: ContentPreviewPopOverProps) => {
  const shouldHide = useIsSize(Sizes.SM, { skipResizeListener: !hideOnMobile });
  const getAdditionalMiddleware = useCallback<
    NonNullable<
      React.ComponentProps<typeof PopOverPrimitive>['getAdditionalMiddleware']
    >
  >(
    (detectOverflowOptions) => [
      contentPreviewPopOverSize({
        ...detectOverflowOptions,
      }),
      flip({
        fallbackAxisSideDirection: allowCrossAxisFlip ? 'end' : 'none',
        ...detectOverflowOptions,
      }),
    ],
    [allowCrossAxisFlip],
  );

  if (shouldHide && hideOnMobile) {
    return <>{children}</>;
  }

  return (
    <PopOverPrimitive
      placement={placement}
      trigger={hoverDisabled ? 'none' : 'hover'}
      offset={offsetAmount}
      getAdditionalMiddleware={getAdditionalMiddleware}
      screenPadding={SCREEN_EDGE_PADDING}
      noFlip
      hideWhenReferenceHidden
      {...props}
    >
      <PopOverPrimitive.Trigger display={display}>
        {children}
      </PopOverPrimitive.Trigger>

      <PopOverPrimitive.Floating
        className={styles.popOver}
        minHeight={minHeight}
        minWidth={minWidth}
        ref={floatingRef}
      >
        <ErrorBoundary name="contentPreviewPopOver">
          <PopOverPrimitive.Content className={styles.popOverContent}>
            {popOver}
          </PopOverPrimitive.Content>
          <PopOverArrow />
        </ErrorBoundary>
      </PopOverPrimitive.Floating>
    </PopOverPrimitive>
  );
};

export default ContentPreviewPopOver;
